import React from "react";
import { Box } from "@mui/joy";
import type {
  Training,
  TrainingsPaginationRequest,
  TrainingSearchRequest,
  TrainingTemplate,
} from "../../../../api/training/Training.typed";
import { TrainingStatusCode } from "../../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import { useTraining, useCustomer } from "../../../../contexts/api";
import {
  DEFAULT_PAGINATION_LIMIT,
  extractDataFromResponse,
  extractPaginationMetadata,
} from "../../../../utils/paginationHelpers";
import Pagination from "../../../common/Pagination";
import ScrollableListPageLayout from "../../../common/ScrollableListPageLayout";
import TrainingsListHeader from "./TrainingsListHeader";
import TrainingsListTable from "./TrainingsListTable";
import { TrainingsListRefreshContext } from "./TrainingsListRefreshContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTrainingsRouteIntent } from "../../common/hooks/useTrainingsRouteIntent";

// Statuses selectable in the list filter (Done lives on the "Cartes" screen).
const FILTERABLE_STATUSES: readonly number[] = [
  TrainingStatusCode.InProgress,
  TrainingStatusCode.Validated,
  TrainingStatusCode.Cancelled,
];

const TrainingsList: React.FC = React.memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeIntent = useTrainingsRouteIntent();
  const highlightTrainingId = routeIntent.targetTrainingId;
  const highlightCustomerId = routeIntent.targetCustomerId;

  const [qualifications, setQualifications] = React.useState<TrainingTemplate[]>();
  const [templateId, setTemplateId] = React.useState<string>(
    routeIntent.trainingTemplateId != null ? String(routeIntent.trainingTemplateId) : "",
  );
  const [status, setStatus] = React.useState<number>(
    routeIntent.trainingStatus != null && FILTERABLE_STATUSES.includes(routeIntent.trainingStatus)
      ? routeIntent.trainingStatus
      : TrainingStatusCode.InProgress,
  );
  const [alertKey, setAlertKey] = React.useState<string>(routeIntent.alertKey ?? "");
  const [alertName, setAlertName] = React.useState<string>(routeIntent.alertName ?? "");
  const [trainings, setTrainings] = React.useState<Training[]>();
  const [customersMap, setCustomersMap] = React.useState<Record<number, ZekayCustomer>>({});
  const [searchInput, setSearchInput] = React.useState<string>(
    typeof routeIntent.searchQuery === "string" ? routeIntent.searchQuery : "",
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(DEFAULT_PAGINATION_LIMIT);

  const { getTrainingsTemplates, getTrainingsByTemplateIdAndStatus, searchTrainings } =
    useTraining();
  const { getCustomersBatch } = useCustomer();

  React.useEffect(() => {
    getTrainingsTemplates().then((templates) => {
      const extractedTemplates = extractDataFromResponse(templates) as TrainingTemplate[];
      setQualifications(extractedTemplates);
    });
  }, [getTrainingsTemplates]);

  // Auto-select the first template once loaded (only on initial empty state).
  React.useEffect(() => {
    if (templateId !== "") return;
    const first = qualifications?.find((t) => t?.ID != null);
    if (first?.ID != null) setTemplateId(String(first.ID));
  }, [qualifications, templateId]);

  const clearAlertFilter = React.useCallback(() => {
    setAlertKey("");
    setAlertName("");
  }, []);

  React.useEffect(() => {
    const state = location.state as typeof routeIntent | null;
    if (!state?.alertKey) return;
    if (state.trainingTemplateId != null) setTemplateId(String(state.trainingTemplateId));
    if (state.trainingStatus != null && FILTERABLE_STATUSES.includes(state.trainingStatus)) {
      setStatus(state.trainingStatus);
    }
    setAlertKey(state.alertKey);
    setAlertName(state.alertName ?? "");
    setSearchInput("");
    setCurrentPage(1);
    navigate(location.pathname, {
      replace: true,
      state: { select: state.select || "Formations" },
    });
  }, [location.state, location.pathname, navigate]);

  const loadTrainings = React.useCallback(
    async (
      templateId: string,
      status: number,
      page = 1,
      limit = itemsPerPage,
      query?: string,
      alert?: string,
    ): Promise<Training[]> => {
      if (!templateId) return [];
      setIsLoading(true);
      try {
        let result;
        if (query && query.trim().length >= 2) {
          const templateIdNum = parseInt(templateId, 10);
          if (!Number.isFinite(templateIdNum) || templateIdNum < 1) {
            setTrainings([]);
            setTotalItems(0);
            setTotalPages(0);
            return [];
          }
          const searchRequest: TrainingSearchRequest = {
            template_id: templateIdNum,
            status_code: status,
            query: query.trim(),
            page,
            limit,
            alert: alert || undefined,
          };
          result = await searchTrainings(searchRequest);
        } else {
          const paginationParams: TrainingsPaginationRequest = {
            page,
            limit,
            alert: alert || undefined,
          };
          result = await getTrainingsByTemplateIdAndStatus(templateId, status, paginationParams);
        }
        const trainingsData = extractDataFromResponse(result) as Training[];
        const ids = (trainingsData ?? [])
          .map((t) => t.CustomerID)
          .filter((id): id is number => id != null);
        const customerIds = Array.from(new Set(ids));
        if (customerIds.length > 0) {
          const batch = await getCustomersBatch(customerIds);
          if (Array.isArray(batch)) {
            const map: Record<number, ZekayCustomer> = {};
            batch.forEach((c) => {
              if (c.Id != null) map[c.Id] = c;
            });
            setCustomersMap(map);
          }
        } else {
          setCustomersMap({});
        }
        setTrainings(trainingsData);
        const metadata = extractPaginationMetadata(result);
        if (metadata) {
          setTotalItems(metadata.total);
          setTotalPages(metadata.total_pages);
          setCurrentPage(page);
        } else {
          setTotalItems(trainingsData.length);
          setTotalPages(1);
          setCurrentPage(1);
        }
        return trainingsData;
      } catch {
        setTrainings([]);
        setTotalItems(0);
        setTotalPages(0);
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [getTrainingsByTemplateIdAndStatus, searchTrainings, getCustomersBatch, itemsPerPage],
  );

  const refreshTrainings = React.useCallback(async (): Promise<Training[]> => {
    if (!templateId) return [];
    return loadTrainings(templateId, status, currentPage, itemsPerPage, searchInput, alertKey);
  }, [templateId, status, currentPage, itemsPerPage, searchInput, alertKey, loadTrainings]);

  const listRefreshContextValue = React.useMemo(
    () => ({ getRefresh: () => refreshTrainings }),
    [refreshTrainings],
  );

  const prevCriteriaRef = React.useRef({
    templateId: "",
    status,
    searchInput: "",
    alertKey: "",
    itemsPerPage,
    currentPage: 1,
  });

  React.useEffect(() => {
    if (!templateId) return;
    const prev = prevCriteriaRef.current;
    const changedCredit = prev.templateId !== templateId;
    const changedStatus = prev.status !== status;
    const changedSearch = prev.searchInput !== searchInput;
    const changedAlert = prev.alertKey !== alertKey;
    const changedPageSize = prev.itemsPerPage !== itemsPerPage;
    const pageToUse =
      changedCredit || changedStatus || changedSearch || changedAlert || changedPageSize
        ? 1
        : currentPage;
    const shouldUpdate =
      changedCredit ||
      changedStatus ||
      changedSearch ||
      changedAlert ||
      changedPageSize ||
      (currentPage !== prev.currentPage && currentPage === pageToUse);
    if (!shouldUpdate) return;
    if (searchInput.trim().length > 0) {
      const timeoutId = setTimeout(() => {
        loadTrainings(templateId, status, pageToUse, itemsPerPage, searchInput, alertKey);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
    loadTrainings(templateId, status, pageToUse, itemsPerPage, undefined, alertKey);
    prevCriteriaRef.current = {
      templateId,
      status,
      searchInput,
      alertKey,
      itemsPerPage,
      currentPage: pageToUse,
    };
  }, [templateId, status, searchInput, alertKey, itemsPerPage, currentPage, loadTrainings]);

  const handleChangeQualification = (newValue: string | null) => {
    setTemplateId(newValue || "");
    setSearchInput("");
    clearAlertFilter();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const updateTrainingLocally = (updatedTraining: Training) => {
    setTrainings((current) =>
      current?.map((t) => (t.CustomerID === updatedTraining.CustomerID ? updatedTraining : t)),
    );
  };

  const removeTrainingLocally = (customerID: number) => {
    setTrainings((current) => current?.filter((t) => t.CustomerID !== customerID));
  };

  const handlePageChange = (newPage: number) => {
    if (templateId && newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newLimit: number) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1);
  };

  return (
    <TrainingsListRefreshContext.Provider value={listRefreshContextValue}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <TrainingsListHeader
          qualifications={qualifications}
          templateId={templateId}
          onTemplateIdChange={handleChangeQualification}
          status={status}
          onStatusChange={(value) => {
            setStatus(value);
            clearAlertFilter();
          }}
          isLoading={isLoading}
          searchInput={searchInput}
          onSearchChange={handleSearch}
          alertName={alertName}
          onClearAlert={clearAlertFilter}
        />
        <ScrollableListPageLayout
          sx={{ flex: 1, minHeight: 0, pt: 1 }}
          isLoading={isLoading}
          pagination={
            templateId && totalPages > 1 ? (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                loading={isLoading}
              />
            ) : undefined
          }
        >
          <TrainingsListTable
            isLoading={isLoading}
            trainings={trainings}
            templateId={templateId}
            customersMap={customersMap}
            status={status}
            setTrainings={setTrainings}
            updateTrainingLocally={updateTrainingLocally}
            removeTrainingLocally={removeTrainingLocally}
            refreshTrainings={refreshTrainings}
            highlightTrainingId={highlightTrainingId}
            highlightCustomerId={highlightCustomerId}
          />
        </ScrollableListPageLayout>
      </Box>
    </TrainingsListRefreshContext.Provider>
  );
});

TrainingsList.displayName = "TrainingsList";

export default TrainingsList;
